import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Link, Image, Code,
  Heading1, Heading2, Heading3, Quote, Undo, Redo,
  Table, FileDown, Printer, Copy, Save
} from 'lucide-react';
import { toast } from 'sonner';

const DocumentEditor = ({ 
  initialContent = '', 
  onSave, 
  onContentChange,
  readOnly = false,
  showToolbar = true 
}) => {
  const editorRef = useRef(null);
  const [content, setContent] = useState(initialContent);

  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  }, []);

  const handleContentChange = useCallback(() => {
    const newContent = editorRef.current?.innerHTML || '';
    setContent(newContent);
    onContentChange?.(newContent);
  }, [onContentChange]);

  const handleSave = useCallback(() => {
    const htmlContent = editorRef.current?.innerHTML || '';
    onSave?.(htmlContent);
    toast.success('Document saved');
  }, [onSave]);

  const insertTable = useCallback(() => {
    const table = `
      <table style="border-collapse: collapse; width: 100%; margin: 10px 0;">
        <tr>
          <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5;">Header 1</th>
          <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5;">Header 2</th>
          <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5;">Header 3</th>
        </tr>
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 1</td>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 2</td>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 3</td>
        </tr>
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 4</td>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 5</td>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 6</td>
        </tr>
      </table>
    `;
    execCommand('insertHTML', table);
  }, [execCommand]);

  const insertLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  }, [execCommand]);

  const insertImage = useCallback(() => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  }, [execCommand]);

  const formatBlock = useCallback((tag) => {
    execCommand('formatBlock', tag);
  }, [execCommand]);

  const exportToHTML = useCallback(() => {
    const htmlContent = editorRef.current?.innerHTML || '';
    const blob = new Blob([`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Policy Document</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
          h2 { color: #444; margin-top: 30px; }
          h3 { color: #555; }
          table { border-collapse: collapse; width: 100%; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          th { background: #f5f5f5; }
          blockquote { border-left: 4px solid #ddd; margin: 20px 0; padding-left: 20px; color: #666; }
          code { background: #f4f4f4; padding: 2px 6px; border-radius: 4px; }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
    `], { type: 'text/html' });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'policy-document.html';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Document exported as HTML');
  }, []);

  const printDocument = useCallback(() => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Document</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; padding: 40px; }
          h1 { color: #333; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ddd; padding: 8px; }
          th { background: #f5f5f5; }
        </style>
      </head>
      <body>
        ${editorRef.current?.innerHTML || ''}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  }, []);

  const copyContent = useCallback(() => {
    const text = editorRef.current?.innerText || '';
    navigator.clipboard.writeText(text);
    toast.success('Content copied to clipboard');
  }, []);

  const ToolbarButton = ({ icon: Icon, onClick, title, active }) => (
    <Button
      type="button"
      variant={active ? 'secondary' : 'ghost'}
      size="sm"
      onClick={onClick}
      title={title}
      className="h-8 w-8 p-0"
    >
      <Icon className="w-4 h-4" />
    </Button>
  );

  const ToolbarDivider = () => (
    <div className="w-px h-6 bg-border mx-1" />
  );

  return (
    <div className="border rounded-lg overflow-hidden bg-background">
      {showToolbar && !readOnly && (
        <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-muted/50">
          {/* History */}
          <ToolbarButton icon={Undo} onClick={() => execCommand('undo')} title="Undo" />
          <ToolbarButton icon={Redo} onClick={() => execCommand('redo')} title="Redo" />
          
          <ToolbarDivider />
          
          {/* Text formatting */}
          <ToolbarButton icon={Bold} onClick={() => execCommand('bold')} title="Bold (Ctrl+B)" />
          <ToolbarButton icon={Italic} onClick={() => execCommand('italic')} title="Italic (Ctrl+I)" />
          <ToolbarButton icon={Underline} onClick={() => execCommand('underline')} title="Underline (Ctrl+U)" />
          <ToolbarButton icon={Strikethrough} onClick={() => execCommand('strikeThrough')} title="Strikethrough" />
          
          <ToolbarDivider />
          
          {/* Headings */}
          <ToolbarButton icon={Heading1} onClick={() => formatBlock('h1')} title="Heading 1" />
          <ToolbarButton icon={Heading2} onClick={() => formatBlock('h2')} title="Heading 2" />
          <ToolbarButton icon={Heading3} onClick={() => formatBlock('h3')} title="Heading 3" />
          
          <ToolbarDivider />
          
          {/* Lists */}
          <ToolbarButton icon={List} onClick={() => execCommand('insertUnorderedList')} title="Bullet List" />
          <ToolbarButton icon={ListOrdered} onClick={() => execCommand('insertOrderedList')} title="Numbered List" />
          
          <ToolbarDivider />
          
          {/* Alignment */}
          <ToolbarButton icon={AlignLeft} onClick={() => execCommand('justifyLeft')} title="Align Left" />
          <ToolbarButton icon={AlignCenter} onClick={() => execCommand('justifyCenter')} title="Align Center" />
          <ToolbarButton icon={AlignRight} onClick={() => execCommand('justifyRight')} title="Align Right" />
          
          <ToolbarDivider />
          
          {/* Insert */}
          <ToolbarButton icon={Link} onClick={insertLink} title="Insert Link" />
          <ToolbarButton icon={Image} onClick={insertImage} title="Insert Image" />
          <ToolbarButton icon={Table} onClick={insertTable} title="Insert Table" />
          <ToolbarButton icon={Quote} onClick={() => formatBlock('blockquote')} title="Quote" />
          <ToolbarButton icon={Code} onClick={() => execCommand('insertHTML', '<code>code</code>')} title="Code" />
          
          <ToolbarDivider />
          
          {/* Actions */}
          <ToolbarButton icon={Copy} onClick={copyContent} title="Copy Text" />
          <ToolbarButton icon={FileDown} onClick={exportToHTML} title="Export HTML" />
          <ToolbarButton icon={Printer} onClick={printDocument} title="Print" />
          
          <div className="flex-1" />
          
          {onSave && (
            <Button size="sm" onClick={handleSave} className="gap-1">
              <Save className="w-4 h-4" />
              Save
            </Button>
          )}
        </div>
      )}
      
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        onInput={handleContentChange}
        dangerouslySetInnerHTML={{ __html: initialContent }}
        className="min-h-[400px] p-4 focus:outline-none prose prose-sm max-w-none"
        style={{
          lineHeight: '1.6',
        }}
      />
    </div>
  );
};

export default DocumentEditor;
